import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {LicensedAgentService} from "../../../shared/services/licensed-agent.service";
import {PartnerService} from "../../../shared/services/partner.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {FilterService} from "../../../shared/services/filter.service";
import {DictService} from "../../../shared/services/dict.service";
import {LicensedAgent} from "../../../shared/interface/licensed-agent";
import {Profile} from "../../../shared/interface/profile";
import {Partner} from "../../../shared/interface/partner";
import {Ofwca} from "../../../shared/interface/ofwca";
import {of, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {delay, takeUntil, tap} from "rxjs/operators";
import {validNip} from "../../../shared/validator/nip-validator";
import {ArrayValidators} from "../../../shared/validator/array-validators";
import {validPhoneNumber} from "../../../shared/validator/phone-validator";
import {PhoneNumber} from "../../../shared/interface/phone-number";
import {EmailAddress} from "../../../shared/interface/email-address";
import {HttpErrorResponse} from "@angular/common/http";
import {ListResponse} from "../../../shared/interface/response/list-response";
import * as uuid from 'uuid';
import {OfwcaService} from "../../../shared/services/ofwca.service";

export interface AddLicensedDialogDate {
  partnerObject: Partner;
}

@Component({
  selector: 'app-add-licensed',
  templateUrl: './add-licensed.component.html',
  styleUrls: ['./add-licensed.component.scss']
})
export class AddLicensedComponent implements OnInit,  OnDestroy, AfterViewInit {

  items: LicensedAgent[] = [];
  profile?: Profile;
  addMode = false;
  partnerObject: Partner;
  licensedAgentForm: FormGroup;
  invoiceNipControl: FormControl;
  licenseNipControl: FormControl;
  licensePayerControl: FormControl;
  ofwcaControl: FormControl;
  phones: FormGroup[] = [];
  emails: FormGroup[] = [];
  partnerOfwca: Ofwca[] = [];
  errors: any = {};
  ofwca = false;
  userPermissions: ReplaySubject<UserPermissions>;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<AddLicensedComponent>,
    private licensedAgentService: LicensedAgentService,
    private partnerService: PartnerService,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: AddLicensedDialogDate,
    private filterService: FilterService,
    private dictService: DictService,
    private ofwcaService: OfwcaService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.partnerObject = data.partnerObject;
    this.licensedAgentForm = fb.group({
      invoiceNip: this.invoiceNipControl = fb.control('', [Validators.required, validNip()]),
      licenseNip: this.licenseNipControl = fb.control('', [Validators.required, validNip()]),
      phones: fb.array([], [ArrayValidators.minLength(1)]),
      emails: fb.array([], [ArrayValidators.minLength(1)]),
      licensePayer: this.licensePayerControl = fb.control('', [Validators.required]),
      partnerShortName: fb.control(this.partnerObject.shortName.toString(), [Validators.required]),
      partnerId: fb.control(this.partnerObject.recordId.toString(), [Validators.required]),
      ofwca: this.ofwcaControl = fb.control('', [Validators.required]),

    });
  }

  ngAfterViewInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });
    this.downloadPartner();
    this.getOfwca();
  }

  ngOnInit(): void {


    this.licensePayerControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((dictId: number) => {
        if (dictId) {
          this.dictService.getKey(dictId)
            .subscribe((dictKey: string) => {
              if (dictKey === 'ofwca') {
                this.ofwcaControl.setValidators([Validators.required]);
                this.ofwcaControl.enable();
                this.ofwca = true;
              } else if (dictKey === 'partner') {
                this.ofwcaControl.setValidators([]);
                this.ofwcaControl.reset();
                this.ofwcaControl.disable();
                this.ofwca = false;
                this.downloadPartner();
              }
            });
        }
      });

    this.ofwcaControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((ofwcaId: string) => {
        if (ofwcaId) {
          this.invoiceNipControl.reset();
          this.licenseNipControl.reset();
          this.phones.splice(0, this.phones.length);
          this.emails.splice(0, this.emails.length);
          this.refreshPhoneControl('phones');
          this.refreshPhoneControl('emails');
          this.ofwcaService.get(ofwcaId)
            .subscribe((ofwca: Ofwca) => {
              this.addPhone('phones', null , (ofwca.phones.filter((el: PhoneNumber) => el.main))[0].number, '');
              this.addEmail('emails', null , ( ofwca.emails.filter((el: EmailAddress) => el.main))[0].address, '');
            });
        }
      });
  }

  addPhone(type: string, recordUuid?: string, phone?: string, name?: string): void {
    this[type].push(this.fb.group({
      uuid: this.fb.control(recordUuid ?? uuid.v4(), [Validators.required]),
      phone: this.fb.control(phone ?? '', [Validators.required, validPhoneNumber()]),
      name: this.fb.control(name ?? ''),
    }));
    this.refreshPhoneControl(type);
  }
  addEmail(type: string, recordUuid?: string, email?: string, name?: string): void {
    this[type].push(this.fb.group({
      uuid: this.fb.control(recordUuid ?? uuid.v4(), [Validators.required]),
      email: this.fb.control(email ?? '', [Validators.required, Validators.email]),
      name: this.fb.control(name ?? ''),
    }));
    this.refreshEmailControl(type);
  }
  closeDialog(e): void {
    this.dialogRef.close(e);
  }

  removePhone(i: number, type: string): void {
    this[type].splice(i, 1);
    this.refreshPhoneControl(type);
  }
  refreshPhoneControl(type: string): void {
    const validators = type === 'phones' ? [ArrayValidators.minLength(1)] : [];
    this.licensedAgentForm.setControl(type, this.fb.array(this[type], validators));
  }
  refreshEmailControl(type: string): void {
    const validators = type === 'emails' ? [ArrayValidators.minLength(1)] : [];
    this.licensedAgentForm.setControl(type, this.fb.array(this[type], validators));
  }
  removeEmail(i: number, type: string): void {
    this[type].splice(i, 1);
    this.refreshEmailControl(type);
  }

  downloadPartner(): void{
    this.phones.splice(0, this.phones.length);
    this.emails.splice(0, this.emails.length);
    this.refreshPhoneControl('phones');
    this.refreshPhoneControl('emails');
    this.licenseNipControl.setValue(this.partnerObject.nip);
    this.invoiceNipControl.setValue(this.partnerObject.nip);
    this.partnerObject.phones.filter((phone: PhoneNumber) => phone.type.key === 'primary')
      .forEach((phone: PhoneNumber) => this.addPhone('phones', phone.uuid, phone.number, phone.name));
    this.partnerObject.emails.filter((email: EmailAddress)  => email.main)
      .forEach((email: EmailAddress) => this.addEmail('emails', email.uuid, email.address, email.name));

  }
  sendForm(): void {
    if (!this.licensedAgentForm.valid) {
      this.licensedAgentForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    this.licensedAgentService.send(this.licensedAgentForm.getRawValue())
      .subscribe(() => {
        this.snack.open('Pomyślnie zgłoszono licencję ', '', {duration: 2000});
        this.addMode = false;
        this.dialogRef.close();
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy dodawaniu zgłoszenia wystąpił błąd', error.status.toString(), {duration: 2000});
        if (error.status === 400) {
          this.snack.open(error.error.error.text, '400', {duration: 4000});
        }
      });
  }

  getOfwca(): void{
    this.partnerOfwca =  [];
    this.partnerService.getPartnerOfwca(this.partnerObject.uuid)
      .subscribe((Ofwcas: ListResponse<Ofwca>) => {
        if (Ofwcas.total > 0) {
          Ofwcas.items.forEach((ofwca: Ofwca) => {
            this.partnerOfwca.push(ofwca);
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
